import React, { useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, Target, AlertCircle, Filter } from 'lucide-react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { projectService } from '@/services/projectService'

const ProjectCalendar = () => {
  // Date range state - default to current month
  const [dateRange, setDateRange] = useState(() => {
    const start = new Date()
    start.setDate(1) // First day of current month
    const end = new Date(start.getFullYear(), start.getMonth() + 3, 0) // Last day of 3 months ahead
    return { startDate: start, endDate: end }
  })
  
  const [viewMode, setViewMode] = useState('timeline')
  const [statusFilter, setStatusFilter] = useState('active')
  const [selectedProjectIds, setSelectedProjectIds] = useState([])
  const [includeTasks, setIncludeTasks] = useState(false)
  const [showProjectDetail, setShowProjectDetail] = useState(null)
  const [showFilters, setShowFilters] = useState(false)

  // Fetch available projects for the multi-select dropdown
  const { data: availableProjects = [] } = useQuery(
    ['available-projects', statusFilter],
    () => projectService.getAvailableProjects({ 
      status: statusFilter !== 'all' ? statusFilter : undefined 
    }),
    { 
      select: (r) => r?.data?.data?.projects || [],
      staleTime: 60000 // Cache for 1 minute
    }
  )

  // Fetch timeline data with date range and filters
  const { data: timelineData, isLoading } = useQuery(
    ['projects-timeline', dateRange, selectedProjectIds, statusFilter, includeTasks],
    () => {
      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0],
        view_mode: 'timeline',
        include_tasks: includeTasks.toString()
      }
      
      if (selectedProjectIds.length > 0) {
        params.project_ids = selectedProjectIds.join(',')
      }
      
      if (statusFilter !== 'all') {
        params.status = statusFilter
      }
      
      return projectService.getProjectsTimeline(params)
    },
    { 
      select: (r) => r?.data?.data || { projects: [], tasks: [], summary: {} },
      keepPreviousData: true
    }
  )

  const projects = timelineData?.projects || []
  const tasks = timelineData?.tasks || []
  const summary = timelineData?.summary || {}


  const safeArray = (data) => {
    if (Array.isArray(data)) return data
    if (!data) return []
    if (typeof data === 'string') { try { return JSON.parse(data) } catch { return [] } }
    return []
  }

  // Generate timeline days based on date range
  const { days, weeks, viewStart, viewEnd } = useMemo(() => {
    const start = new Date(dateRange.startDate)
    start.setHours(0, 0, 0, 0)
    const end = new Date(dateRange.endDate)
    end.setHours(0, 0, 0, 0)
    
    // Calculate total days
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1
    const displayDays = Math.max(42, totalDays) // Minimum 6 weeks
    
    const days = []
    const weeks = []
    
    for (let i = 0; i < displayDays; i++) {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      days.push(d)
      if (i % 7 === 0) weeks.push({ start: new Date(d), days: [] })
      weeks[weeks.length - 1].days.push(d)
    }
    
    return { 
      days, 
      weeks, 
      viewStart: start, 
      viewEnd: days[days.length - 1] 
    }
  }, [dateRange])
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Get upcoming deadlines from timeline data
  const upcomingDeadlines = useMemo(() => {
    const deadlines = []
    const nextTwoWeeks = new Date(today)
    nextTwoWeeks.setDate(today.getDate() + 14)

    projects.forEach(p => {
      const endDate = new Date(p.end_date)
      if (endDate >= today && endDate <= nextTwoWeeks) {
        deadlines.push({ 
          type: 'project', 
          date: endDate, 
          name: p.name, 
          id: p.id, 
          status: p.status,
          code: p.code
        })
      }
    })
    
    // Add task deadlines if tasks are included
    if (includeTasks) {
      tasks.forEach(t => {
        const dueDate = new Date(t.due_date)
        if (dueDate >= today && dueDate <= nextTwoWeeks && t.status !== 'completed') {
          deadlines.push({ 
            type: 'task', 
            date: dueDate, 
            name: t.title, 
            projectId: t.project_id,
            id: t.id
          })
        }
      })
    }
    
    return deadlines.sort((a, b) => a.date - b.date)
  }, [projects, tasks, today, includeTasks])

  // Calculate bar position for timeline
  const getBarStyle = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const viewStartTime = viewStart.getTime()
    const viewEndTime = viewEnd.getTime()
    const totalDays = days.length

    if (end < viewStart || start > viewEnd) return null

    const clampedStart = Math.max(start.getTime(), viewStartTime)
    const clampedEnd = Math.min(end.getTime(), viewEndTime)
    const startOffset = Math.floor((clampedStart - viewStartTime) / (1000 * 60 * 60 * 24))
    const duration = Math.ceil((clampedEnd - clampedStart) / (1000 * 60 * 60 * 24)) + 1

    return { left: `${(startOffset / totalDays) * 100}%`, width: `${(duration / totalDays) * 100}%` }
  }

  const statusColors = {
    planning: { bg: 'bg-slate-400', light: 'bg-slate-100', hex: '#6B7280' },
    active: { bg: 'bg-blue-500', light: 'bg-blue-100', hex: '#3B82F6' },
    on_hold: { bg: 'bg-amber-500', light: 'bg-amber-100', hex: '#F59E0B' },
    completed: { bg: 'bg-green-500', light: 'bg-green-100', hex: '#10B981' },
    cancelled: { bg: 'bg-red-400', light: 'bg-red-100', hex: '#EF4444' }
  }

  // Date range navigation helpers
  const shiftDateRange = (direction) => {
    const days = direction === 'forward' ? 30 : -30
    setDateRange(prev => ({
      startDate: new Date(prev.startDate.getTime() + days * 24 * 60 * 60 * 1000),
      endDate: new Date(prev.endDate.getTime() + days * 24 * 60 * 60 * 1000)
    }))
  }

  const goToToday = () => {
    const start = new Date()
    start.setDate(1)
    const end = new Date(start.getFullYear(), start.getMonth() + 3, 0)
    setDateRange({ startDate: start, endDate: end })
  }

  const setPresetRange = (preset) => {
    const start = new Date()
    let end = new Date()
    
    switch (preset) {
      case 'month':
        start.setDate(1)
        end = new Date(start.getFullYear(), start.getMonth() + 1, 0)
        break
      case 'quarter':
        start.setDate(1)
        end = new Date(start.getFullYear(), start.getMonth() + 3, 0)
        break
      case 'year':
        start.setMonth(0, 1)
        end = new Date(start.getFullYear(), 11, 31)
        break
      default:
        break
    }
    
    setDateRange({ startDate: start, endDate: end })
  }

  const handleProjectSelection = (projectId) => {
    setSelectedProjectIds(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId)
      } else {
        return [...prev, projectId]
      }
    })
  }

  const clearProjectSelection = () => {
    setSelectedProjectIds([])
  }

  const formatWeekLabel = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const formatDateLabel = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

  const isToday = (date) => date.toDateString() === today.toDateString()
  const todayOffset = Math.floor((today.getTime() - viewStart.getTime()) / (1000 * 60 * 60 * 24))

  if (isLoading) return <div className="p-6 text-center">Loading projects timeline...</div>

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarIcon className="h-6 w-6" />
            Project Timeline
          </h1>
          <p className="text-gray-500">Timeline view with date range filtering</p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Filter className="h-4 w-4" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <div className="space-y-2">
                <DatePicker
                  selected={dateRange.startDate}
                  onChange={(date) => setDateRange(prev => ({ ...prev, startDate: date }))}
                  selectsStart
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  dateFormat="MMM d, yyyy"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholderText="Start Date"
                />
                <DatePicker
                  selected={dateRange.endDate}
                  onChange={(date) => setDateRange(prev => ({ ...prev, endDate: date }))}
                  selectsEnd
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  minDate={dateRange.startDate}
                  dateFormat="MMM d, yyyy"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholderText="End Date"
                />
              </div>
              <div className="flex gap-1 mt-2">
                <button onClick={() => setPresetRange('month')} className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded">Month</button>
                <button onClick={() => setPresetRange('quarter')} className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded">Quarter</button>
                <button onClick={() => setPresetRange('year')} className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded">Year</button>
              </div>
            </div>

            {/* Project Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Projects ({selectedProjectIds.length > 0 ? selectedProjectIds.length : 'All'})
              </label>
              <div className="border rounded-lg max-h-40 overflow-y-auto p-2 space-y-1">
                {availableProjects.length === 0 ? (
                  <p className="text-sm text-gray-500 p-2">No projects available</p>
                ) : (
                  availableProjects.map(project => (
                    <label key={project.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedProjectIds.includes(project.id)}
                        onChange={() => handleProjectSelection(project.id)}
                        className="rounded"
                      />
                      <span className="text-sm truncate">{project.code} - {project.name}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedProjectIds.length > 0 && (
                <button onClick={clearProjectSelection} className="text-xs text-blue-600 hover:underline mt-1">
                  Clear selection
                </button>
              )}
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select 
                value={statusFilter} 
                onChange={e => setStatusFilter(e.target.value)} 
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="planning">Planning</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
              <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeTasks}
                  onChange={(e) => setIncludeTasks(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Include Tasks</span>
              </label>
              <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                <div className="text-gray-600">Total: {summary.total_projects || 0} projects</div>
                <div className="text-green-600">Active: {summary.active_projects || 0}</div>
                {includeTasks && <div className="text-blue-600">Tasks: {summary.total_tasks || 0}</div>}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Upcoming Deadlines */}
      {upcomingDeadlines.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Coming Up (Next 2 Weeks)
          </h3>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {upcomingDeadlines.slice(0, 8).map((d, i) => {
              const daysUntil = Math.ceil((d.date - today) / (1000 * 60 * 60 * 24))
              const isUrgent = daysUntil <= 3
              return (
                <Link 
                  key={i} 
                  to={`/projects/${d.projectId || d.id}`}
                  className={`flex-shrink-0 p-3 rounded-lg border ${isUrgent ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'} hover:shadow-sm transition-shadow min-w-[180px]`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {d.type === 'task' ? (
                      <Target className="h-4 w-4 text-purple-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-blue-500" />
                    )}
                    <span className={`text-xs font-medium ${isUrgent ? 'text-red-600' : 'text-gray-500'}`}>
                      {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}
                    </span>
                  </div>
                  <p className="font-medium text-sm text-gray-900 truncate">{d.name}</p>
                  {d.code && <p className="text-xs text-gray-500 truncate">{d.code}</p>}
                  <p className="text-xs text-gray-400 mt-1">
                    {d.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* Timeline Navigation */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => shiftDateRange('backward')} 
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Previous 30 days"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button 
              onClick={goToToday} 
              className="px-3 py-1.5 text-sm font-medium bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Today
            </button>
            <button 
              onClick={() => shiftDateRange('forward')} 
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Next 30 days"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <span className="text-sm font-medium text-gray-600">
            {formatDateLabel(dateRange.startDate)} - {formatDateLabel(dateRange.endDate)}
          </span>
        </div>


        {/* Week Headers */}
        <div className="flex border-b bg-gray-50">
          <div className="w-56 flex-shrink-0 p-2 border-r text-xs font-medium text-gray-500">Project</div>
          <div className="flex-1 flex">
            {weeks.map((week, wi) => (
              <div key={wi} className="flex-1 text-center py-2 border-r text-xs font-medium text-gray-500">
                {formatWeekLabel(week.start)}
              </div>
            ))}
          </div>
        </div>

        {/* Day Headers */}
        <div className="flex border-b">
          <div className="w-56 flex-shrink-0 border-r"></div>
          <div className="flex-1 flex relative">
            {days.map((day, di) => (
              <div 
                key={di} 
                className={`flex-1 text-center py-1 text-xs border-r ${
                  isToday(day) 
                    ? 'bg-blue-100 font-bold text-blue-700' 
                    : day.getDay() === 0 || day.getDay() === 6 
                      ? 'bg-gray-50 text-gray-400' 
                      : 'text-gray-500'
                }`}
              >
                {day.getDate()}
              </div>
            ))}
            {/* Today marker */}
            {todayOffset >= 0 && todayOffset < days.length && (
              <div 
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10" 
                style={{ left: `${((todayOffset + 0.5) / days.length) * 100}%` }}
              ></div>
            )}
          </div>
        </div>

        {/* Project Rows */}
        <div className="max-h-[500px] overflow-y-auto">
          {projects.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {selectedProjectIds.length > 0 
                ? 'No projects found for the selected filters' 
                : 'No projects in this date range'}
            </div>
          ) : (
            projects.map(project => {
              const barStyle = getBarStyle(project.start_date, project.end_date)
              const colors = statusColors[project.status] || statusColors.planning

              return (
                <div 
                  key={project.id} 
                  className="flex border-b hover:bg-gray-50 group cursor-pointer" 
                  onClick={() => setShowProjectDetail(project)}
                >
                  {/* Project Info */}
                  <div className="w-56 flex-shrink-0 p-3 border-r">
                    <span className="font-medium text-gray-900 hover:text-blue-600 block truncate text-sm">
                      {project.name}
                    </span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400">{project.code}</span>
                      <span className={`h-2 w-2 rounded-full ${colors.bg}`}></span>
                    </div>
                  </div>

                  {/* Timeline Bar */}
                  <div className="flex-1 relative h-14">
                    {/* Grid */}
                    <div className="absolute inset-0 flex">
                      {days.map((day, di) => (
                        <div 
                          key={di} 
                          className={`flex-1 border-r ${
                            day.getDay() === 0 || day.getDay() === 6 ? 'bg-gray-50/50' : ''
                          }`}
                        ></div>
                      ))}
                    </div>

                    {/* Today line */}
                    {todayOffset >= 0 && todayOffset < days.length && (
                      <div 
                        className="absolute top-0 bottom-0 w-0.5 bg-red-500/30 z-10" 
                        style={{ left: `${((todayOffset + 0.5) / days.length) * 100}%` }}
                      ></div>
                    )}

                    {/* Project Bar */}
                    {barStyle && (
                      <div className="absolute top-2 h-6" style={barStyle}>
                        <div 
                          className={`h-full rounded-full ${colors.bg} shadow-sm flex items-center px-2 overflow-hidden`}
                          style={{ backgroundColor: project.color }}
                        >
                          <span className="text-xs text-white font-medium truncate">
                            {project.progress_percentage || 0}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>


        {/* Legend */}
        <div className="p-3 bg-gray-50 border-t flex flex-wrap items-center gap-4 text-xs">
          <span className="font-medium text-gray-600">Legend:</span>
          {Object.entries(statusColors).slice(0, 4).map(([status, colors]) => (
            <div key={status} className="flex items-center gap-1">
              <div className={`h-3 w-6 rounded-full ${colors.bg}`}></div>
              <span className="capitalize text-gray-600">{status.replace('_', ' ')}</span>
            </div>
          ))}
          <div className="flex items-center gap-1">
            <div className="w-0.5 h-4 bg-red-500"></div>
            <span className="text-gray-600">Today</span>
          </div>
        </div>
      </div>

      {/* Project Detail Modal */}
      {showProjectDetail && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" 
          onClick={() => setShowProjectDetail(null)}
        >
          <div 
            className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto m-4" 
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-xs text-gray-500">{showProjectDetail.code}</span>
                  <h2 className="text-xl font-bold text-gray-900">{showProjectDetail.name}</h2>
                </div>
                <button 
                  onClick={() => setShowProjectDetail(null)} 
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="font-medium capitalize">{showProjectDetail.status?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Progress</p>
                  <p className="font-medium">{showProjectDetail.progress_percentage || 0}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Start Date</p>
                  <p className="font-medium">
                    {new Date(showProjectDetail.start_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">End Date</p>
                  <p className="font-medium">
                    {new Date(showProjectDetail.end_date).toLocaleDateString()}
                  </p>
                </div>
                {showProjectDetail.manager && (
                  <div>
                    <p className="text-xs text-gray-500">Manager</p>
                    <p className="font-medium">{showProjectDetail.manager.name}</p>
                  </div>
                )}
                {showProjectDetail.department && (
                  <div>
                    <p className="text-xs text-gray-500">Department</p>
                    <p className="font-medium">{showProjectDetail.department.name}</p>
                  </div>
                )}
                {showProjectDetail.budget_allocated && (
                  <div>
                    <p className="text-xs text-gray-500">Budget</p>
                    <p className="font-medium">
                      ₦{Number(showProjectDetail.budget_allocated).toLocaleString()}
                    </p>
                  </div>
                )}
                {showProjectDetail.team_size > 0 && (
                  <div>
                    <p className="text-xs text-gray-500">Team Size</p>
                    <p className="font-medium">{showProjectDetail.team_size} members</p>
                  </div>
                )}
              </div>
              {showProjectDetail.description && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p className="text-sm text-gray-700">{showProjectDetail.description}</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <button 
                onClick={() => setShowProjectDetail(null)} 
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
              <Link 
                to={`/projects/${showProjectDetail.id}`} 
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                View Full Details
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectCalendar
